// @react
import React, { useRef } from 'react'
// @typings
import { GetUserQuery } from 'lib/graphql/graphqlTypes'
import { CreateAccountFormValues } from 'modules/common/forms/types/createAccount'
// @libraries
import {
  Control,
  Controller,
  FieldErrors,
  UseFormMethods,
} from 'react-hook-form'
import Hidden from '@material-ui/core/Hidden'
// @components
import Grid from '@bit/atd.web.grid'
import InputText from '@bit/atd.web.forms.input-text'
import InputPassword from '@bit/atd.web.forms.input-password'
// @module components
import parse from 'html-react-parser'
import makeStyles from '@material-ui/core/styles/makeStyles'
import colors from '@bit/atd.web.colors/dist/colors'
import strings from 'lib/constants/strings'

const useStyles = makeStyles(() => ({
  clientError: {
    '& > a': {
      color: colors.gray500,
    },
  },
}))

type PropsType = {
  formMethods: UseFormMethods<CreateAccountFormValues>
  control: Control<CreateAccountFormValues>
  errors: FieldErrors
  userData?: GetUserQuery
}

const EmailField = ({ control, errors, userData }: PropsType) => {
  /******************************************
   * REFS
   ******************************************/

  const emailRef = useRef<HTMLInputElement | null>(null)
  /******************************************
   * HOOKS
   ******************************************/
  const classes = useStyles()

  /******************************************
   * RENDER
   ******************************************/
  return (
    <>
      <Controller
        control={control}
        onFocus={() => emailRef?.current?.focus()}
        name="primaryEmailAddress"
        render={({ onChange, onBlur, value }) => (
          <InputText
            name="primaryEmailAddress"
            label="Email Address"
            placeholder={strings.PROFILE.ACCESS.PRIMARY_EMAIL_PLACEHOLDER}
            type="email"
            required
            disabled={!!userData?.user?.id}
            onBlur={onBlur}
            onChange={onChange}
            value={value}
            inputRef={emailRef}
            error={!!errors?.primaryEmailAddress}
            errorText={
              <span className={classes.clientError}>
                {parse(errors?.primaryEmailAddress?.message || '')}
              </span>
            }
          />
        )}
      />
    </>
  )
}

const AccountFields = ({
  formMethods,
  control,
  errors,
  userData,
}: PropsType) => {
  /******************************************
   * REFS
   ******************************************/

  const firstNameRef = useRef<HTMLInputElement | null>(null)
  const lastNameRef = useRef<HTMLInputElement | null>(null)
  const passwordRef = useRef<HTMLInputElement | null>(null)
  const passwordConfirmRef = useRef<HTMLInputElement | null>(null)

  return (
    <>
      <Grid container spacing={4}>
        {/* First and Last Name */}
        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name="firstName"
            onFocus={() => firstNameRef?.current?.focus()}
            render={({ onChange, onBlur, value }) => (
              <InputText
                name="firstName"
                label="First Name"
                placeholder={strings.PROFILE.BIO.ENTER_FIRST_NAME}
                required
                disabled={!!userData?.user?.id}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                inputRef={firstNameRef}
                error={!!errors?.firstName}
                errorText={errors?.firstName?.message}
              />
            )}
          />
        </Grid>

        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name="lastName"
            onFoucs={() => lastNameRef?.current?.focus()}
            render={({ onChange, onBlur, value }) => (
              <InputText
                name="lastName"
                label="Last Name"
                placeholder={strings.PROFILE.BIO.ENTER_LAST_NAME}
                required
                disabled={!!userData?.user?.id}
                onBlur={onBlur}
                onChange={onChange}
                value={value}
                inputRef={lastNameRef}
                error={!!errors?.lastName}
                errorText={errors?.lastName?.message}
              />
            )}
          />
        </Grid>

        {/* Email Address */}
        <Grid xs={12} sm={6} item>
          <EmailField
            formMethods={formMethods}
            control={control}
            userData={userData}
            errors={errors}
          />
        </Grid>

        <Hidden xsDown>
          <Grid sm={6} item />
        </Hidden>

        {/* Create and Retype Password */}
        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name="password"
            onFocus={() => passwordRef?.current?.focus()}
            render={({ onChange, onBlur, value }) => (
              <InputPassword
                name="password"
                label="Create Password"
                placeholder={strings.PROFILE.ACCESS.NEW_PASSWORD_PLACEHOLDER}
                required
                disabled={!!userData?.user?.id}
                onBlur={onBlur}
                onChange={onChange}
                value={!userData?.user?.id ? value : ''}
                inputRef={passwordRef}
                helperText={
                  <span>
                    {parse(strings.PROFILE.ACCESS.PASSWORD_STRENGTH_DESC)}
                  </span>
                }
                error={!userData?.user?.id && !!errors?.password}
                errorText={errors?.password?.message}
              />
            )}
          />
        </Grid>

        <Grid xs={12} sm={6} item>
          <Controller
            control={control}
            name="passwordRetype"
            onFocus={() => passwordConfirmRef?.current?.focus()}
            render={({ onChange, onBlur, value }) => (
              <InputPassword
                name="passwordRetype"
                label="Re-type Password"
                placeholder={strings.PROFILE.ACCESS.PASSWORD_RETYPE_PLACEHOLDER}
                required
                disabled={!!userData?.user?.id}
                onBlur={onBlur}
                onChange={onChange}
                value={!userData?.user?.id ? value : '•••••••'}
                inputRef={passwordConfirmRef}
                error={!userData?.user?.id && !!errors?.passwordRetype}
                errorText={errors?.passwordRetype?.message}
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default AccountFields
